import { DateTimeRangePicker } from "@mui/x-date-pickers-pro";
import { useCallback, useState, type ComponentProps } from "react";
import type { Dayjs } from "dayjs";
import type { PickerChangeHandlerContext } from "@mui/x-date-pickers/models";
import type { DateTimeRangeValidationError } from "@mui/x-date-pickers-pro/models";

export type SafeDateTimeRangePickerProps = Omit<
  ComponentProps<typeof DateTimeRangePicker>,
  "onChange"
> & {
  onChange?: (
    value: [Dayjs | null, Dayjs | null],
    context: PickerChangeHandlerContext<DateTimeRangeValidationError>
  ) => void;
  onInvalidDate?: () => void;
};

export function SafeDateTimeRangePicker({
  onChange,
  onInvalidDate,
  value: parentValue,
  ...rest
}: SafeDateTimeRangePickerProps) {
  const [localValue, setLocalValue] = useState<[Dayjs | null, Dayjs | null]>(
    parentValue ?? [null, null]
  );
  const [trackedParentMs, setTrackedParentMs] = useState<
    [number | undefined, number | undefined]
  >([parentValue?.[0]?.valueOf(), parentValue?.[1]?.valueOf()]);

  const currentParentStartMs = parentValue?.[0]?.valueOf();
  const currentParentEndMs = parentValue?.[1]?.valueOf();
  if (
    currentParentStartMs !== trackedParentMs[0] ||
    currentParentEndMs !== trackedParentMs[1]
  ) {
    setLocalValue(parentValue ?? [null, null]);
    setTrackedParentMs([currentParentStartMs, currentParentEndMs]);
  }

  const handleChange = useCallback(
    (
      value: [Dayjs | null, Dayjs | null],
      context: PickerChangeHandlerContext<DateTimeRangeValidationError>
    ) => {
      setLocalValue(value);

      const hasValidationError =
        context.validationError[0] != null ||
        context.validationError[1] != null;
      const isValidStartValue = value[0] == null || value[0].isValid();
      const isValidEndValue = value[1] == null || value[1].isValid();

      if (hasValidationError || !isValidStartValue || !isValidEndValue) {
        onInvalidDate?.();
        return;
      }

      onChange?.(value, context);
    },
    [onChange, onInvalidDate]
  );

  return (
    <DateTimeRangePicker {...rest} value={localValue} onChange={handleChange} />
  );
}
